import React, { useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Pill, Text } from "@foris/avocado-suite";
import { Origin, ProcessStat, Scenario, Workspace } from "@models/ISchema";
import styles from "./contextModalDetailsBox.module.scss";

interface ContextModalDetailsBoxProps {
  canAccessEDH: boolean;
  hasOriginSelector?: boolean;
  origin?: ProcessStat | Origin;
  scenario?: Scenario;
  workspace?: Workspace;
}

const ContextModalDetailsBox = ({
  canAccessEDH,
  hasOriginSelector,
  origin,
  scenario,
  workspace,
}: ContextModalDetailsBoxProps) => {
  const { t } = useTranslation();

  const originDetails = useMemo(() => {
    if (!origin) return null;

    if ("attributes" in origin) {
      return origin;
    }

    const originData = origin as Origin;

    return {
      id: originData?.id,
      label: originData?.label,
      attributes: {
        isProductive: !!originData?.isProductive,
        isProtected: !!originData?.isProtected,
        isReal: !!originData?.isReal,
        isVisible: !!originData?.isVisible,
      },
    };
  }, [origin]);

  const workspaceDetails = useMemo(() => {
    if (origin && "scenario" in origin) {
      return origin?.scenario?.workspace;
    }

    return workspace ?? null;
  }, [originDetails, workspace]);

  const scenarioDetails = useMemo(() => {
    if (origin && "scenario" in origin) {
      return origin?.scenario;
    }

    return scenario ?? null;
  }, [originDetails, scenario]);

  const showProductivePill = useMemo(() => {
    if (canAccessEDH && originDetails?.attributes?.isProductive) {
      return true;
    }

    if (!canAccessEDH && scenarioDetails?.status?.value === "PRODUCTIVE") {
      return true;
    }

    return false;
  }, [canAccessEDH, scenarioDetails, originDetails]);

  return (
    <div
      className={cx(
        styles.contextModalDetailsBox,
        hasOriginSelector && styles.contextModalDetailsBox__hasOrginSelector,
      )}
    >
      <Text className={styles.contextModalDetailsBox_title} type="xs" weight="medium">
        {t("context-modal.details.title")}:
      </Text>

      <div className={styles.contextModalDetailsBox_info}>
        <div className={styles.mainInfo}>
          <Text className={styles.mainInfo_item} type="sm">
            {t("context-modal.details.workspace")}: <strong>{workspaceDetails?.name ?? "-"}</strong>
          </Text>
          <Text className={styles.mainInfo_item} type="sm">
            {t("context-modal.details.scenario")}:{" "}
            <strong>{`${scenarioDetails?.id ?? "-"} ${scenarioDetails?.name ?? ""}`}</strong>
          </Text>
          {!!originDetails && (
            <Text className={styles.mainInfo_item} type="sm">
              {t("context-modal.details.origin")}:{" "}
              <strong>{`${originDetails?.id ?? "-"} ${originDetails?.label ?? ""}`}</strong>
            </Text>
          )}
        </div>

        {(!!originDetails || showProductivePill) && (
          <div className={styles.infoPills}>
            {showProductivePill && (
              <Pill icon="star" iconFilled iconColor="neutral-50" className={styles.pill}>
                {t("context-modal.details.productive")}
              </Pill>
            )}

            {!!originDetails && (
              <Pill iconColor="neutral-50" className={styles.pill}>
                {originDetails?.attributes?.isReal
                  ? t("context-modal.details.origin-real")
                  : t("context-modal.details.origin-simulated")}
              </Pill>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextModalDetailsBox;
